import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'

import { Alert, Button, Stack, TextField, useToaster } from '@forto-dev/phoenix-ui-core'

import { isValidFormTranslationKey, useLocalisation } from '@/utils/localisation'

import { ResetPasswordSchema, ResetPasswordSchemaType } from './resetPassword.schema'
import { ResetPasswordRouteSearchParams } from './route'
import { useResetPassword } from '../../api/useResetPassword'

export const ResetPasswordForm = ({ resetPasswordParams }: { resetPasswordParams: ResetPasswordRouteSearchParams }) => {
  const navigate = useNavigate()
  const { translate, translateFormValidation } = useLocalisation()
  const toaster = useToaster()
  const requestPassword = useResetPassword()
  const [error, setError] = useState<string | undefined>(undefined)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })
  const onSubmit: SubmitHandler<ResetPasswordSchemaType> = async (data) => {
    setError(undefined)
    try {
      await requestPassword.mutateAsync({
        password: data.password,
        digest: resetPasswordParams.digest as string,
        expiry: resetPasswordParams.expiry as number,
        user_id: resetPasswordParams.user_id as string,
        customer_id: resetPasswordParams.customer_id as string,
      })
      toaster({
        title: translate('reset-password.notifications.redirecting'),
      })
      setTimeout(() => {
        navigate({
          to: '/login',
        })
      }, 3000)
    } catch (e) {
      if (e instanceof Error && e.message) {
        setError(e.message)
      }
    }
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={16}>
        {error ? <Alert title={error} variant="negative" /> : null}
        <TextField
          label={translate('reset-password.fields.password.label')}
          placeholder={translate('reset-password.fields.password.placeholder')}
          {...register('password')}
          type="password"
          autoComplete="new-password"
          disabled={requestPassword.isPending}
          state={errors.password ? 'negative' : undefined}
          hint={
            isValidFormTranslationKey(errors.password?.message)
              ? translateFormValidation(errors.password.message)
              : errors.password?.message
          }
        />
        <TextField
          label={translate('reset-password.fields.confirm-password.label')}
          placeholder={translate('reset-password.fields.confirm-password.placeholder')}
          {...register('confirmPassword')}
          type="password"
          autoComplete="new-password"
          disabled={requestPassword.isPending}
          state={errors.confirmPassword ? 'negative' : undefined}
          hint={
            isValidFormTranslationKey(errors.confirmPassword?.message)
              ? translateFormValidation(errors.confirmPassword.message)
              : errors.confirmPassword?.message
          }
        />
        <Button type="submit" disabled={requestPassword.isPending}>
          {translate('reset-password.actions.resetPassword')}
        </Button>
      </Stack>
    </form>
  )
}
